import { useEffect, useRef, type FC } from "react";
import {
  usePlatformConfig,
  usePlatformConfigHost
} from "@kivunova/kivufrontendcommon";
import { useRetrievePlatformConfig } from "../hooks/api/useRetrievePlatformConfig";

const registryFingerprint = (
  stage: string | null,
  businessTypes: { key: string; enabled: boolean }[],
  featureFlags: Record<string, boolean>,
  businessType: string | null | undefined,
  sellingMode: string[],
  baseUnit: string[]
): string =>
  JSON.stringify({
    stage,
    businessTypes,
    featureFlags,
    businessType: businessType ?? null,
    sellingMode,
    baseUnit
  });

/** Public platform config — fetch once on mount (outside AuthProvider). */
const HostPlatformConfigSync: FC = () => {
  const { config } = usePlatformConfig();
  const {
    setPlatformConfig,
    setPlatformConfigLoadError,
    setPlatformConfigLoading
  } = usePlatformConfigHost();
  const retrieve = useRetrievePlatformConfig();
  const appliedRegistryKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setPlatformConfigLoading(retrieve.isPending);
  }, [retrieve.isPending, setPlatformConfigLoading]);

  useEffect(() => {
    if (retrieve.isError) {
      setPlatformConfigLoadError(true);
      return;
    }

    if (!retrieve.data) {
      return;
    }

    const sellingMode = config?.sellingMode ?? [];
    const baseUnit = config?.baseUnit ?? [];
    const nextKey = registryFingerprint(
      retrieve.data.stage,
      retrieve.data.businessTypes,
      retrieve.data.featureFlags,
      config?.businessType,
      sellingMode,
      baseUnit
    );

    if (appliedRegistryKeyRef.current === nextKey) {
      return;
    }
    appliedRegistryKeyRef.current = nextKey;

    setPlatformConfig({
      stage: retrieve.data.stage,
      businessTypes: retrieve.data.businessTypes,
      featureFlags: retrieve.data.featureFlags,
      businessType: config?.businessType ?? null,
      sellingMode,
      baseUnit
    });
    setPlatformConfigLoadError(false);
  }, [
    config?.businessType,
    config?.sellingMode,
    config?.baseUnit,
    retrieve.data,
    retrieve.isError,
    setPlatformConfig,
    setPlatformConfigLoadError
  ]);

  return null;
};

export default HostPlatformConfigSync;
